<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core'
import { computed, ref } from 'vue'
import { RouteLocationRaw, useRoute } from 'vue-router'

import { useAuthStore } from '@/stores/auth-store'
import { LicenseType } from '@/types/company'
import { moduleInformation, moduleLicenses } from '@/utils/licenses'
import { useAlarmStore } from '@/stores/alarm-store'

import SidebarNavLink from '@/components/layout/navigation/SidebarNavLink.vue'
import ProfileDropdown from '@/components/auth/ProfileDropdown.vue'

defineProps<{
    isOpen?: boolean
}>()

const emit = defineEmits<{
    close: []
}>()

const authStore = useAuthStore()
const alarmStore = useAlarmStore()
const route = useRoute()
const sidebar = ref<HTMLElement>()

const alarmRoute = computed<RouteLocationRaw | null>(() => {
    if (route.name?.toString().includes('packaging')) {
        return { name: 'packaging.alarms' }
    } else if (route.name?.toString().includes('dmr')) {
        return { name: 'dmr.alarms' }
    } else if (route.name?.toString().includes('dm.')) {
        return { name: 'dm.alarms' }
    }

    if (authStore.hasLicense([LicenseType.PackagingModule, LicenseType.PackagingModuleReadonly])) {
        return { name: 'packaging.alarms' }
    } else if (
        authStore.hasLicense([
            LicenseType.DamageReportModule,
            LicenseType.DamageReportModuleReadonly,
        ])
    ) {
        return { name: 'dmr.alarms' }
    } else if (
        authStore.hasLicense([
            LicenseType.DeliveryManagementModule,
            LicenseType.DeliveryManagementModuleReadonly,
        ])
    ) {
        return { name: 'dm.alarms' }
    }

    return null
})

onClickOutside(sidebar, () => emit('close'))
</script>

<template>
    <aside
        ref="sidebar"
        class="fixed inset-y-0 left-0 z-50 w-64 transform bg-dark-700 text-primary-200 transition-transform duration-300 ease-in-out lg:translate-x-0"
        :class="{
            'translate-x-0': isOpen,
            '-translate-x-full': !isOpen,
        }"
    >
        <!-- Header with Animated Livrero Logo -->
        <div class="relative px-3 py-4 mb-2">
            <!-- Logo -->
            <div>
                <div class="group cursor-pointer select-none px-3">
                    <h1
                        class="text-3xl font-bold text-primary-200 mb-2 transition-all duration-500 group-hover:text-yellow-500 group-hover:drop-shadow-xl"
                    >
                        <span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 0ms"
                            >L</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 100ms"
                            >i</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 200ms"
                            >v</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 300ms"
                            >r</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 400ms"
                            >e</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 500ms"
                            >r</span
                        ><span
                            class="logo-letter transition-all duration-300 ease-out inline-block group-hover:animate-logo-bounce group-hover:text-yellow-500"
                            style="animation-delay: 600ms"
                            >o</span
                        >
                    </h1>

                    <div
                        class="h-1 w-20 bg-gradient-to-r from-yellow-500 to-yellow-400 rounded-full transition-all duration-700 group-hover:w-32 group-hover:h-2 group-hover:shadow-lg group-hover:shadow-yellow-500/50 group-hover:animate-pulse"
                    ></div>
                </div>
            </div>

            <!-- Close button for mobile - repositioned to top right -->
            <button
                class="lg:hidden text-primary-200 hover:text-yellow-500 absolute top-2 right-5"
                @click="emit('close')"
            >
                <MdiClose class="h-5 w-5" />
            </button>
        </div>

        <!-- Navigation -->
        <nav class="flex-1 space-y-1 px-3 py-4">
            <SidebarNavLink
                :to="{ name: 'packaging' }"
                :translation="moduleInformation[LicenseType.PackagingModule].translation"
                :activated="
                    authStore.hasLicense([
                        LicenseType.PackagingModule,
                        LicenseType.PackagingModuleReadonly,
                    ])
                "
            >
                <MdiWarehouse class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'dmr' }"
                :translation="moduleInformation[LicenseType.DamageReportModule].translation"
                :activated="
                    authStore.hasLicense([
                        LicenseType.DamageReportModule,
                        LicenseType.DamageReportModuleReadonly,
                    ])
                "
            >
                <MdiTruckCargoContainer class="h-5 w-5" />
            </SidebarNavLink>

            <!-- Additional navigation items -->
            <SidebarNavLink
                :to="authStore.hasLicense(moduleLicenses) ? { name: 'dashboards' } : ''"
                :translation="moduleInformation[LicenseType.DashboardModule].translation"
                :activated="authStore.hasLicense([LicenseType.DashboardModule])"
            >
                <MdiViewDashboard class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'dr' }"
                :translation="moduleInformation[LicenseType.DriverReportModule].translation"
                :activated="
                    authStore.hasLicense([
                        LicenseType.DriverReportModule,
                        LicenseType.DriverReportModuleReadonly,
                    ])
                "
            >
                <MdiTimerMarkerOutline class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'dm' }"
                :translation="moduleInformation[LicenseType.DeliveryManagementModule].translation"
                :activated="
                    authStore.hasLicense([
                        LicenseType.DeliveryManagementModule,
                        LicenseType.DeliveryManagementModuleReadonly,
                    ])
                "
            >
                <MdiMapMarker class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'pod' }"
                :translation="moduleInformation[LicenseType.PODModule].translation"
                :activated="
                    authStore.hasLicense([LicenseType.PODModule, LicenseType.PODModuleReadonly])
                "
            >
                <MdiFileDocumentCheckOutline class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="{ name: 'ecmr' }"
                :translation="moduleInformation[LicenseType.ECMRModule].translation"
                :activated="authStore.hasLicense(LicenseType.ECMRModule)"
            >
                <MdiFileCertificateOutline class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                v-if="authStore.hasLicense(LicenseType.CargoLoadingModule)"
                :to="authStore.hasLicense(moduleLicenses) ? { name: 'cl' } : ''"
                :translation="moduleInformation[LicenseType.CargoLoadingModule].translation"
                :activated="authStore.hasLicense([LicenseType.CargoLoadingModule])"
            >
                <MdiPackage class="h-5 w-5" />
            </SidebarNavLink>

            <SidebarNavLink
                :to="
                    authStore.hasLicense(moduleLicenses) || authStore.isReadonly.globalReadonly
                        ? { name: 'settings' }
                        : ''
                "
                translation="settings"
                :activated="
                    authStore.hasLicense(moduleLicenses) || authStore.isReadonly.globalReadonly
                "
            >
                <MdiCog class="h-5 w-5" />
            </SidebarNavLink>

            <!-- Alarms Link -->
            <SidebarNavLink
                v-if="authStore.companyId && alarmRoute"
                :to="alarmRoute"
                translation="alarms"
                :activated="true"
            >
                <div class="relative">
                    <MdiBell v-if="alarmStore.counts.total > 0" class="h-5 w-5" />
                    <MdiBellOutline v-else class="h-5 w-5" />

                    <span
                        v-if="alarmStore.counts.total > 0"
                        class="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold"
                        v-text="alarmStore.counts.total"
                    />
                </div>
            </SidebarNavLink>
        </nav>

        <!-- Modern User Profile Section -->
        <div class="p-4 mt-auto border-t border-gray-700/30">
            <!-- User Info with Profile Dropdown -->
            <ProfileDropdown placement="top-left" class="block w-full mb-3">
                <template #trigger>
                    <div
                        class="group flex items-center w-full p-2.5 rounded-lg hover:bg-gray-800/40 transition-all duration-200 cursor-pointer"
                    >
                        <div class="flex-shrink-0">
                            <div
                                class="h-9 w-9 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center group-hover:from-gray-600 group-hover:to-gray-700 transition-all duration-200 shadow-sm"
                            >
                                <MdiAccount class="h-4 w-4 text-gray-300" />
                            </div>
                        </div>
                        <div class="ml-3 flex-1 min-w-0">
                            <p
                                class="text-sm font-medium text-white group-hover:text-gray-100 transition-colors leading-tight"
                            >
                                {{ authStore.user?.name }}
                            </p>
                            <p class="text-xs text-gray-400 truncate mt-0.5">
                                {{ authStore.company?.name }}
                            </p>
                        </div>
                        <div class="flex items-center ml-2">
                            <!-- Dropdown Chevron -->
                            <div class="p-1.5">
                                <MdiChevronDown
                                    class="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-300 transition-colors"
                                />
                            </div>
                        </div>
                    </div>
                </template>
            </ProfileDropdown>
        </div>
    </aside>

    <!-- Mobile backdrop -->
    <div
        v-if="isOpen"
        class="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
        @click="emit('close')"
    />
</template>

<style scoped>
/* Logo Animation - adapted for sidebar */
@keyframes logo-bounce {
    0%,
    100% {
        transform: translateY(0) scale(1);
        text-shadow: 0 0 0 rgba(255, 209, 40, 0);
    }
    25% {
        transform: translateY(-4px) scale(1.05);
        text-shadow: 0 2px 4px rgba(255, 209, 40, 0.3);
    }
    50% {
        transform: translateY(-6px) scale(1.1);
        text-shadow: 0 4px 8px rgba(255, 209, 40, 0.4);
    }
    75% {
        transform: translateY(-3px) scale(1.025);
        text-shadow: 0 3px 6px rgba(255, 209, 40, 0.3);
    }
}

.animate-logo-bounce {
    animation: logo-bounce 0.8s ease-out;
}
</style>
